// framework/client/components/GenericFormRenderer.js
import { ComponentBase } from "../core/ComponentBase.js";

export class GenericFormRenderer extends ComponentBase {

    static styles = `
        .form-floating {
            position: relative;
        }
        .form-floating > label,
        .form-floating > label::after {
            background: transparent !important;
            background-color: transparent !important;
        }
        .form-floating > label::after {
            display: none !important;
            content: none !important;
        }
        .form-floating > .form-control,
        .form-floating > .form-select {
            height: calc(3.5rem + 2px) !important;
            line-height: 1.25 !important;
            padding: 1.625rem 0.75rem 0.625rem !important;
        }
        .form-floating > label {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: auto !important;
            max-width: calc(100% - 1.5rem) !important;
            height: auto !important;
            padding: 1rem 0.75rem !important;
            pointer-events: none !important;
            transform-origin: 0 0 !important;
            transition: opacity 0.15s ease-in-out, transform 0.15s ease-in-out, color 0.15s ease-in-out !important;
            color: var(--bs-secondary-color, rgba(255, 255, 255, 0.65)) !important;
            z-index: 5 !important;
        }
        .form-floating > .form-control:focus ~ label,
        .form-floating > .form-control:not(:placeholder-shown) ~ label,
        .form-floating > .form-control:-webkit-autofill ~ label,
        .form-floating > .form-select ~ label {
            opacity: 0.85 !important;
            transform: scale(0.75) translateY(-0.65rem) translateX(0.15rem) !important;
        }
        .form-floating > .form-control:focus ~ label {
            color: var(--bs-primary, #6366f1) !important;
        }
        .form-floating > .form-control:focus ~ label .required-indicator {
            color: var(--bs-primary, #6366f1) !important;
        }
        .form-check-group {
            padding-top: 0.5rem;
        }
        .gfr-field-container {
            margin-bottom: 0.5rem;
        }
        .invalid-feedback {
            display: block;
            min-height: 1.25em;
            font-size: 0.875em;
            margin-top: 0.25rem;
        }
        .invalid-feedback:not(:empty) {
            color: var(--bs-danger, #ef4444);
        }
    `;


    constructor(config) {
        super(config);
        this.formConfig = this.config.formConfig;
        this.schema = this.config.schema;
        this.errors = {};
        this.touched = new Set();
        this._boundHandleSubmit = this._handleSubmit.bind(this);
        this._boundHandleInteraction = this._handleFieldInteraction.bind(this);
        this.fieldComponents = new Map();
        this.modalInstance = null;
        this.isSubmitting = false;
        this.submitButton = null;
        this.applyStyles();
    }


    async initialize() {
        await super.initialize();
    }

    async render() {
        if (this.config.layout === 'dialog') {
            if (!this.container) {
                this.container = document.createElement("div");
                this.container.className = 'modal fade';
                this.container.id = this.config.formId;
                this.container.setAttribute('tabindex', '-1');

                this.container.innerHTML = `
                    <div class="modal-dialog modal-dialog-centered">
                        <div class="modal-content">
                            <form novalidate></form>
                        </div>
                    </div>
                `;
                this.formElement = this.container.querySelector('form');
                this.modalInstance = new bootstrap.Modal(this.container);

                this.container.addEventListener('hidden.bs.modal', () => {
                    this.destroy();
                });
            }
            this.formElement.innerHTML = '';

            const { title, fields = [] } = this.formConfig;
            let headerHtml = '';
            if (title) {
                headerHtml = `
                    <div class="modal-header">
                        <h5 class="modal-title">${this.t(title)}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>`;
            }

            const bodyContainer = document.createElement('div');
            bodyContainer.className = 'modal-body';
            const fieldPromises = fields.map(fieldConfig => this._renderSingleField(fieldConfig, ''));
            const fieldElements = await Promise.all(fieldPromises);
            fieldElements.forEach(el => bodyContainer.appendChild(el));

            const footerHtml = `
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">${this.t('Cancel')}</button>
                    <button type="submit" class="btn btn-primary">${this.t(this.formConfig.submitButtonLabel || "Submit")}</button>
                </div>`;

            this.formElement.innerHTML = headerHtml;
            this.formElement.appendChild(bodyContainer);
            this.formElement.insertAdjacentHTML('beforeend', footerHtml);

        } else {
            if (!this.container) {
                this.container = document.createElement("form");
                this.container.id = this.config.formId;
                this.container.noValidate = true;
                this.formElement = this.container;
            }
            this.formElement.innerHTML = '';

            const { fields = [], formTemplate, title, description, links, submitButtonLabel, showActions = true } = this.formConfig;

            if (title) {
                const titleElement = document.createElement('h2');
                titleElement.className = 'mb-3';
                titleElement.textContent = this.t(title);
                this.formElement.appendChild(titleElement);
            }
            if (description) {
                const descriptionElement = document.createElement('p');
                descriptionElement.className = 'text-muted mb-4';
                descriptionElement.textContent = this.t(description);
                this.formElement.appendChild(descriptionElement);
            }

            const hpContainer = document.createElement('div');
            hpContainer.style.cssText = 'position: absolute; left: -9999px; top: -9999px; opacity: 0; pointer-events: none; height: 0; width: 0; overflow: hidden;';
            hpContainer.setAttribute('aria-hidden', 'true');
            hpContainer.setAttribute('tabindex', '-1');
            hpContainer.innerHTML = `
                <input type="text" name="_hp_field" tabindex="-1" autocomplete="off" data-lpignore="true" data-1p-ignore="true" data-bwignore="true" value="" />
                <input type="text" name="form_ref" tabindex="-1" autocomplete="off" data-lpignore="true" data-1p-ignore="true" data-bwignore="true" value="" />
            `;
            this.formElement.appendChild(hpContainer);

            const fieldsContainer = document.createElement('div');
            const fieldPromises = fields.map(fieldConfig => this._renderSingleField(fieldConfig, ''));
            const fieldElements = await Promise.all(fieldPromises);
            fieldElements.forEach(el => fieldsContainer.appendChild(el));
            this.formElement.appendChild(fieldsContainer);

            if (showActions) {
                const footerContainer = document.createElement('div');
                if (links && Array.isArray(links)) {
                    const linksContainer = document.createElement('div');
                    linksContainer.className = 'd-flex justify-content-between align-items-center mb-4';
                    links.forEach(link => {
                        const linkElement = document.createElement('a');
                        linkElement.href = this.appClient.layoutEngine.prefix + link.href;
                        linkElement.textContent = this.t(link.text);
                        linksContainer.appendChild(linkElement);
                    });
                    footerContainer.appendChild(linksContainer);
                }

                const submitButtonContainer = document.createElement('div');
                submitButtonContainer.className = 'd-grid mt-2';
                const submitButton = document.createElement('button');
                submitButton.type = 'submit';
                submitButton.className = 'btn btn-primary';
                submitButton.textContent = this.t(submitButtonLabel || "Submit");
                submitButtonContainer.appendChild(submitButton);
                footerContainer.appendChild(submitButtonContainer);

                this.formElement.appendChild(footerContainer);
            }
        }

        this.submitButton = this.formElement.querySelector('button[type="submit"]');
        this.formElement.addEventListener("submit", this._boundHandleSubmit);
        if (this.formConfig.validation?.validateOnChange) {
            this.formElement.addEventListener("input", this._boundHandleInteraction);
            this.formElement.addEventListener("focusout", this._boundHandleInteraction);
        }

        this.appClient.tx.applyTranslations(this.container);

        if (this.config.layout === 'dialog') {
            this.modalInstance.show();
        }

        return this.container;
    }

    async _renderSingleField(fieldConfig, error) {
        const { name, type = 'text', label, component } = fieldConfig;
        const container = document.createElement('div');

        if (component) {
            container.className = 'mb-3';
            const labelEl = document.createElement('label');
            labelEl.className = 'form-label';
            labelEl.textContent = this.t(label);
            container.appendChild(labelEl);

            const componentInstance = await this.createManagedComponent(component, null, {
                instanceId: `${this.config.formId}-${name}`,
                ...fieldConfig
            });

            this.fieldComponents.set(name, componentInstance);
            const componentElement = await componentInstance.render();
            container.appendChild(componentElement);

        } else {
            container.innerHTML = this._renderSingleFieldHtml(fieldConfig, error);
        }
        return container;
    }

    _renderSingleFieldHtml(fieldConfig, error) {
        const { name, type = 'text', label, required, autocomplete, options, rows, placeholder } = fieldConfig;
        const labelMode = this.formConfig.labelMode || 'standard';
        const inputId = `${this.config.formId}-${name}`;
        const isInvalidClass = error ? 'is-invalid' : '';
        const requiredAttr = required ? "required" : "";
        const autocompleteAttr = autocomplete ? `autocomplete="${autocomplete}"` : "";
        const placeholderAttr = (labelMode === 'floating') ? 'placeholder=" "' : (placeholder ? `placeholder="${this.t(placeholder)}"` : `placeholder="${this.t(label)}"`);

        const isSchemaRequired = this.schema?.shape?.[name]?._def?.typeName !== 'ZodOptional';
        const requiredIndicator = isSchemaRequired ? ' <span class="required-indicator">*</span>' : '';

        // --- MODIFICATION: The error message is now always present in the DOM ---
        const errorHtml = `<div class="invalid-feedback">${this.t(error || '')}</div>`; // Render empty if no error

        const gapClass = fieldConfig.gapClass ?? this.formConfig.gapClass ?? 'gfr-field-container';

        let fieldHtml = '';
        switch (type) {
            case 'textarea':
                fieldHtml = `<textarea id="${inputId}" name="${name}" class="form-control ${isInvalidClass}" rows="${rows || 3}" ${placeholderAttr} ${requiredAttr}></textarea>`;
                break;
            case 'select':
                const optionHtml = (options || []).map(opt => `<option value="${opt.value}">${this.t(opt.label)}</option>`).join('');
                fieldHtml = `<select id="${inputId}" name="${name}" class="form-select ${isInvalidClass}" ${requiredAttr}>${optionHtml}</select>`;
                break;
            case 'checkbox':
            case 'boolean':
                const checkedAttr = fieldConfig.defaultValue ? 'checked' : '';
                return `
                    <div class="form-check form-check-group ${gapClass}">
                        <input type="checkbox" id="${inputId}" name="${name}" class="form-check-input ${isInvalidClass}" ${checkedAttr}>
                        <label for="${inputId}" class="form-check-label">${this.t(label)}${requiredIndicator}</label>
                        ${errorHtml}
                    </div>
                `;
            case 'otp':
                fieldHtml = `<input type="text" id="${inputId}" name="${name}" class="form-control text-center fs-3 fw-bold ${isInvalidClass}" style="letter-spacing: 0.35em; font-family: monospace;" inputmode="numeric" pattern="[0-9]*" maxlength="6" ${placeholderAttr} ${requiredAttr} autocomplete="one-time-code">`;
                break;
            default:
                if (name === 'otpCode') {
                    fieldHtml = `<input type="${type}" id="${inputId}" name="${name}" class="form-control text-center fs-3 fw-bold ${isInvalidClass}" style="letter-spacing: 0.35em; font-family: monospace;" inputmode="numeric" pattern="[0-9]*" maxlength="6" ${placeholderAttr} ${requiredAttr} autocomplete="one-time-code">`;
                } else {
                    fieldHtml = `<input type="${type}" id="${inputId}" name="${name}" class="form-control ${isInvalidClass}" ${placeholderAttr} ${requiredAttr} ${autocompleteAttr}>`;
                }
                break;
        }

        if (labelMode === 'floating') {
            // --- MODIFICATION: Add gfr-field-container class ---
            return `
                <div class="form-floating ${gapClass}">
                    ${fieldHtml}
                    <label for="${inputId}">${this.t(label)}${requiredIndicator}</label>
                    ${errorHtml}
                </div>
            `;
        }

        // --- MODIFICATION: Add gfr-field-container class ---
        return `
            <div class="${gapClass}">
                <label for="${inputId}" class="form-label">${this.t(label)}${requiredIndicator}</label>
                ${fieldHtml}
                ${errorHtml}
            </div>
        `;
    }

    _handleFieldInteraction(event) {
        if (event.target.name) {
            this.touched.add(event.target.name);
            this._validate();
        }
    }

    async _handleSubmit(event) {
        event.preventDefault();
        if (this.isSubmitting) return;

        this.setSubmitting(true);
        try {
            if (await this.isFormValid()) {
                this.emit('formSubmit');
            } else {
                this.log.warn("Submit blocked due to form validation errors.");
                this.setSubmitting(false);
            }
        } catch (e) {
            this.log.error("Unexpected error during submit validation:", e);
            this.setSubmitting(false);
        }
    }

    _validate(isSubmit = false) {
        if (!this.schema) return true;
        const userData = this.getFormData();
        const result = this.schema.safeParse(userData);

        const newErrors = {};
        if (!result.success) {
            result.error.errors.forEach(err => {
                const fieldName = err.path[0];
                if (this.touched.has(fieldName) || isSubmit) {
                    newErrors[fieldName] = err.message;
                }
            });
        }

        this.errors = newErrors;
        this._displayErrors();
        return result.success;
    }

    _displayErrors() {
        if (!this.formElement) return;
        const allFieldNames = this.formConfig.fields.map(f => f.name);

        allFieldNames.forEach(fieldName => {
            const inputElement = this.formElement.querySelector(`[name="${fieldName}"]`);
            if (inputElement) {
                const errorContainer = inputElement.closest('.mb-3, .form-floating, .form-check')?.querySelector('.invalid-feedback');
                const errorMsg = this.errors[fieldName];
                if (errorMsg) {
                    inputElement.classList.add('is-invalid');
                    if (errorContainer) errorContainer.textContent = this.t(errorMsg);
                } else {
                    inputElement.classList.remove('is-invalid');
                    if (errorContainer) errorContainer.textContent = '';
                }
            }
        });
    }

    setSubmitting(isSubmitting) {
        this.isSubmitting = isSubmitting;
        if (!this.submitButton) return;

        if (isSubmitting) {
            this.submitButton.disabled = true;
            this.submitButton.innerHTML = `
                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span class="ms-1" data-i18n="...">${this.t('Processing...')}</span>
            `;
        } else {
            this.submitButton.disabled = false;
            this.submitButton.innerHTML = this.t(this.formConfig.submitButtonLabel || "Submit");
        }
    }

    setData(data) {
        if (!this.formElement || !data) return;
        this.formConfig.fields.forEach(fieldConfig => {
            const fieldName = fieldConfig.name;
            const inputElement = this.formElement.querySelector(`[name="${fieldName}"]`);
            if (inputElement) {
                const value = data[fieldName];
                if (inputElement.type === 'checkbox') inputElement.checked = !!value;
                else if (value !== undefined && value !== null) inputElement.value = value;
                else inputElement.value = '';
            }
        });
        this.fieldComponents.forEach((componentInstance, fieldName) => {
            if (data.hasOwnProperty(fieldName) && typeof componentInstance.setValue === 'function') {
                componentInstance.setValue(data[fieldName]);
            }
        });
    }

    getFormData() {
        if (!this.formElement) return {};
        const formData = new FormData(this.formElement);
        const data = Object.fromEntries(formData.entries());
        this.formConfig.fields.forEach(field => {
            if (field.type === 'checkbox' || field.type === 'boolean') {
                data[field.name] = this.formElement.querySelector(`[name="${field.name}"]`).checked;
            }
        });
        this.fieldComponents.forEach((componentInstance, fieldName) => {
            if (typeof componentInstance.getValue === 'function') {
                data[fieldName] = componentInstance.getValue();
            }
        });
        return data;
    }

    isFormValid() {
        if (!this.schema) return true;
        this.formConfig.fields.forEach(field => this.touched.add(field.name));
        return this._validate(true);
    }

    setFieldErrors(errors) {
        this.errors = {};
        for (const fieldName in errors) {
            this.errors[fieldName] = errors[fieldName];
        }
        this._displayErrors();
    }


    clear() {
        if (this.formElement) this.formElement.reset();
        this.touched.clear();
        this.errors = {};
        this._displayErrors();
    }

    destroy() {
        if (this.modalInstance) {
            // Check if the modal is still shown to avoid re-triggering hidden.bs.modal loop
            if (this.modalInstance._isShown) {
                this.modalInstance.hide();
            }
            this.modalInstance.dispose();
            this.modalInstance = null;
        }
        if (this.container) {
            this.container.remove();
            this.container = null;
        }

        // Emit a 'destroyed' event so parents can clean up their listeners
        this.emit('destroyed');

        // Let the base class and component manager handle final cleanup
        super.destroy();
    }


}